import { detectPwaPushNotificationSupport } from '@/shared/lib/pwa';

export type PushPermissionState = NotificationPermission;

export type BrowserPushSupport =
  | {
      gate: 'ready';
      permission: PushPermissionState;
    }
  | {
      gate: 'requires-ios-standalone';
    }
  | {
      gate: 'unsupported';
    };

export interface BrowserPushSubscription {
  endpoint: string;
  p256dh: string | null;
  auth: string | null;
  unsubscribe(): Promise<boolean>;
}

export interface BrowserPushAdapter {
  getSupport(): BrowserPushSupport;
  requestPermission(): Promise<PushPermissionState>;
  getSubscription(): Promise<BrowserPushSubscription | null>;
  subscribe(applicationServerKey: Uint8Array): Promise<BrowserPushSubscription>;
}

export const unsubscribeAndVerify = async (
  browser: BrowserPushAdapter,
  subscription: BrowserPushSubscription,
): Promise<boolean> => {
  try {
    if (await subscription.unsubscribe()) {
      return true;
    }
  } catch {
    // A rejected unsubscribe can still mean that the browser already removed the subscription.
  }

  try {
    return (await browser.getSubscription()) === null;
  } catch {
    return false;
  }
};

const arrayBufferToBase64Url = (value: ArrayBuffer): string => {
  const bytes = new Uint8Array(value);
  let binary = '';

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
};

const toBrowserSubscription = (subscription: PushSubscription): BrowserPushSubscription => {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  return {
    endpoint: subscription.endpoint,
    p256dh: p256dh ? arrayBufferToBase64Url(p256dh) : null,
    auth: auth ? arrayBufferToBase64Url(auth) : null,
    unsubscribe: () => subscription.unsubscribe(),
  };
};

export const base64UrlToUint8Array = (value: string): Uint8Array => {
  if (!/^[A-Za-z0-9_-]+$/u.test(value)) {
    throw new Error('Invalid base64url');
  }

  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replaceAll('-', '+').replaceAll('_', '/');
  const raw = atob(base64);
  const bytes = Uint8Array.from(raw, (character) => character.charCodeAt(0));

  // VAPID uses an uncompressed P-256 public key.
  if (bytes.byteLength !== 65 || bytes[0] !== 4) {
    throw new Error('Invalid VAPID public key');
  }

  return bytes;
};

// `serviceWorker.ready` никогда не резолвится, пока нет зарегистрированного SW
// (dev без VITE_PWA_DEV_ENABLED, неудачная регистрация на проде) — операции ниже
// сначала проверяют регистрацию через getRegistration(), который резолвится всегда.
type PushServiceWorkerContainer = Pick<ServiceWorkerContainer, 'getRegistration' | 'ready'>;

export const createBrowserPushAdapter = (
  container: PushServiceWorkerContainer = navigator.serviceWorker,
): BrowserPushAdapter => ({
  getSupport: () => {
    const hasNotification = 'Notification' in window;
    const gate = detectPwaPushNotificationSupport();

    return gate === 'ready'
      ? {
          gate,
          permission: hasNotification ? Notification.permission : 'denied',
        }
      : { gate };
  },
  requestPermission: () => Notification.requestPermission(),
  getSubscription: async () => {
    const registration = await container.getRegistration();
    if (!registration) {
      return null;
    }

    const subscription = await registration.pushManager.getSubscription();

    return subscription ? toBrowserSubscription(subscription) : null;
  },
  subscribe: async (applicationServerKey) => {
    if (!(await container.getRegistration())) {
      throw new Error('Service worker is not registered');
    }

    // Регистрация есть — дожидаемся активации: subscribe() требует активный SW.
    const registration = await container.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    return toBrowserSubscription(subscription);
  },
});
